import React, { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { appStore } from "@/app_store";
import { VirtualKeyboard } from "@/components/toolkit/VirtualKeyboard";
import { isTouchDevice } from "@/utils/device";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import "@xterm/xterm/css/xterm.css";
import "./s.terminal_modal.css";

const IS_TOUCH = isTouchDevice();

interface Props {
    sessionId: string;
    isNew: boolean;
    isOpen: boolean;
    label: string;
    onClose: () => void;
}

export const TerminalModal = observer(function TerminalModal({ sessionId, isNew, isOpen, label, onClose }: Props) {
    const xtermRef = useRef<HTMLDivElement>(null);
    const vkbSpacerRef = useRef<HTMLDivElement>(null);
    const sendInputRef = useRef<((data: string) => void) | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);
    const [vkbVisible, setVkbVisible] = useState(false);

    const sendCtrlC = useCallback(() => {
        sendInputRef.current?.("\x03");
    }, []);

    const sendTab = useCallback(() => {
        sendInputRef.current?.("\t");
    }, []);

    const sendCtrlD = useCallback(() => {
        sendInputRef.current?.("\x04");
    }, []);

    const sendCtrlZ = useCallback(() => {
        sendInputRef.current?.("\x1a");
    }, []);

    const sendArrowUp = useCallback(() => {
        sendInputRef.current?.("\x1b[A");
    }, []);

    const sendArrowDown = useCallback(() => {
        sendInputRef.current?.("\x1b[B");
    }, []);

    const handleVkbKey = useCallback((key: string) => {
        const data = key === 'Enter' ? '\r' : key === 'Backspace' ? '\x7f' : key;
        sendInputRef.current?.(data);
    }, []);

    const handleVkbHeightChange = useCallback((height: number) => {
        if (vkbSpacerRef.current) {
            vkbSpacerRef.current.style.height = `${height}px`;
        }
        requestAnimationFrame(() => fitAddonRef.current?.fit());
    }, []);

    useEffect(() => {
        if (!isOpen) setVkbVisible(false);
    }, [isOpen]);

    useEffect(() => {
        if (!vkbVisible) return;
        const handler = (e: PointerEvent) => {
            const vkbEl = document.querySelector(".vkb");
            if (vkbEl && vkbEl.contains(e.target as Node)) return;
            if (xtermRef.current && xtermRef.current.contains(e.target as Node)) return;
            setVkbVisible(false);
        };
        document.addEventListener("pointerdown", handler, { capture: true });
        return () => document.removeEventListener("pointerdown", handler, { capture: true });
    }, [vkbVisible]);

    useEffect(() => {
        if (!isOpen || !xtermRef.current) return;

        let disposed = false;

        const term = new Terminal({
            theme: { background: "#0d0d0d" },
            fontFamily: '"JetBrains Mono", "Fira Code", Menlo, monospace',
            fontSize: 13,
            cursorBlink: true,
        });
        const fitAddon = new FitAddon();
        fitAddonRef.current = fitAddon;
        term.loadAddon(fitAddon);
        term.open(xtermRef.current);
        fitAddon.fit();

        let nativeKbdObserver: MutationObserver | undefined;
        let touchCleanup: (() => void) | undefined;
        if (IS_TOUCH) {
            const cont = xtermRef.current!;
            const suppressNativeKeyboard = () => {
                cont.querySelectorAll<HTMLTextAreaElement>('textarea').forEach(ta => {
                    ta.setAttribute('inputmode', 'none');
                });
            };
            suppressNativeKeyboard();
            nativeKbdObserver = new MutationObserver(suppressNativeKeyboard);
            nativeKbdObserver.observe(cont, { childList: true, subtree: true });

            let lastTouchY = 0;
            let touchMoved = false;
            let scrollAccum = 0;
            let pinchActive = false;
            let pinchStartDist = 0;
            let pinchStartFontSize = 13;

            // Inertia state
            const velSamples: Array<{ dy: number; t: number }> = [];
            let inertiaRafId: number | null = null;

            const cancelInertia = () => {
                if (inertiaRafId !== null) {
                    cancelAnimationFrame(inertiaRafId);
                    inertiaRafId = null;
                }
            };

            const getPinchDist = (touches: TouchList) => {
                const dx = touches[0].clientX - touches[1].clientX;
                const dy = touches[0].clientY - touches[1].clientY;
                return Math.sqrt(dx * dx + dy * dy);
            };

            const onTouchStart = (e: TouchEvent) => {
                cancelInertia();
                velSamples.length = 0;
                if (e.touches.length === 2) {
                    pinchActive = true;
                    pinchStartDist = getPinchDist(e.touches);
                    pinchStartFontSize = term.options.fontSize ?? 13;
                    touchMoved = true; // prevent tap-to-focus when pinching
                    return;
                }
                if (e.touches.length !== 1) return;
                pinchActive = false;
                suppressNativeKeyboard();
                lastTouchY = e.touches[0].clientY;
                touchMoved = false;
                scrollAccum = 0;
            };

            const onTouchMove = (e: TouchEvent) => {
                if (pinchActive && e.touches.length === 2) {
                    e.preventDefault();
                    const dist = getPinchDist(e.touches);
                    const newSize = Math.max(8, Math.min(32, Math.round(pinchStartFontSize * dist / pinchStartDist)));
                    if (newSize !== term.options.fontSize) {
                        term.options.fontSize = newSize;
                        fitAddon.fit();
                    }
                    return;
                }
                if (e.touches.length !== 1) return;
                const currentY = e.touches[0].clientY;
                const dy = lastTouchY - currentY;
                lastTouchY = currentY;
                if (Math.abs(dy) > 1) {
                    touchMoved = true;
                    const lineHeight = (term.options.fontSize || 13) * 1.5;
                    scrollAccum += dy / lineHeight;
                    const lines = Math.trunc(scrollAccum);
                    if (lines !== 0) {
                        term.scrollLines(lines);
                        scrollAccum -= lines;
                    }
                    velSamples.push({ dy, t: performance.now() });
                    if (velSamples.length > 8) velSamples.shift();
                    e.preventDefault();
                }
            };

            const onTouchEnd = (e: TouchEvent) => {
                if (e.touches.length < 2) pinchActive = false;
                if (e.touches.length !== 0) return;
                if (!touchMoved) {
                    term.focus();
                    setVkbVisible(true);
                    return;
                }
                // Compute velocity (px/ms) from samples within last 150ms
                const now = performance.now();
                const recent = velSamples.filter(s => now - s.t < 150);
                velSamples.length = 0;
                let vel = 0;
                if (recent.length >= 2) {
                    const span = recent[recent.length - 1].t - recent[0].t;
                    const totalDy = recent.reduce((sum, s) => sum + s.dy, 0);
                    if (span > 0) vel = totalDy / span;
                }
                if (Math.abs(vel) < 0.05) return;
                // Animate inertia: decay velocity by FRICTION per 60fps frame
                const FRICTION = 0.93;
                let lastFrameTime = performance.now();
                const animate = () => {
                    const t = performance.now();
                    const dt = t - lastFrameTime;
                    lastFrameTime = t;
                    vel *= Math.pow(FRICTION, dt / 16.667);
                    if (Math.abs(vel) < 0.005) {
                        inertiaRafId = null;
                        return;
                    }
                    const lineHeight = (term.options.fontSize || 13) * 1.5;
                    scrollAccum += (vel * dt) / lineHeight;
                    const lines = Math.trunc(scrollAccum);
                    if (lines !== 0) {
                        term.scrollLines(lines);
                        scrollAccum -= lines;
                    }
                    inertiaRafId = requestAnimationFrame(animate);
                };
                inertiaRafId = requestAnimationFrame(animate);
            };

            cont.addEventListener('touchstart', onTouchStart, { passive: true });
            cont.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
            cont.addEventListener('touchend', onTouchEnd, { passive: true });
            touchCleanup = () => {
                cont.removeEventListener('touchstart', onTouchStart);
                cont.removeEventListener('touchmove', onTouchMove, { capture: true });
                cont.removeEventListener('touchend', onTouchEnd);
                cancelInertia();
            };
        }

        const ro = new ResizeObserver(() => {
            if (!disposed) fitAddon.fit();
        });
        ro.observe(xtermRef.current);

        const sessionStore = appStore.agent.session.project?.deployment?.terminal_sessions.find(
            s => s.id === sessionId
        );

        const sendInput = (data: string) => {
            sessionStore?.send_keys(data);
        };
        sendInputRef.current = sendInput;

        const inputDispose = term.onData(sendInput);

        const resizeDispose = term.onResize(({ rows, cols }) => {
            sessionStore?.resize(String(cols), String(rows));
        });

        // Replay existing output chunks, then react to new ones
        const seenCount = { value: 0 };

        const writeChunks = (from: number, to: number) => {
            if (!sessionStore) return;
            for (let i = from; i < to; i++) {
                const encoded = sessionStore.output_chunks[i];
                if (!encoded) continue;
                try {
                    const binary = atob(encoded);
                    const bytes = new Uint8Array(binary.length);
                    for (let j = 0; j < binary.length; j++) bytes[j] = binary.charCodeAt(j);
                    term.write(bytes);
                } catch {
                    // ignore decode errors
                }
            }
        };

        // Write any chunks that arrived before this mount
        if (sessionStore) {
            const existing = sessionStore.output_chunks.length;
            writeChunks(0, existing);
            seenCount.value = existing;
        }

        // Subscribe to future chunks
        const disposeChunkReaction = sessionStore
            ? reaction(
                () => sessionStore.output_chunks.length,
                (len) => {
                    if (len > seenCount.value) {
                        writeChunks(seenCount.value, len);
                        seenCount.value = len;
                    }
                }
            )
            : null;

        return () => {
            disposed = true;
            ro.disconnect();
            nativeKbdObserver?.disconnect();
            touchCleanup?.();
            inputDispose.dispose();
            resizeDispose.dispose();
            disposeChunkReaction?.();
            fitAddonRef.current = null;
            sendInputRef.current = null;
            term.dispose();
        };
    }, [isOpen, sessionId, isNew]);

    const isInContext = appStore.state.isContextRef(sessionId);
    const toggleTerminalContext = () => {
        appStore.state.toggleContextRef({ type: "terminal", id: sessionId, label: label || sessionId });
    };

    const contextFloatBtn = (
        <button
            className={"terminal_modal__context_btn" + (isInContext ? " terminal_modal__context_btn--active" : "")}
            onClick={toggleTerminalContext}
            title={isInContext ? "Remove from agent context" : "Add to agent context"}
        >@</button>
    );

    if (IS_TOUCH) {
        return (
            <div className={"terminal_modal " + SWIPE_GUARD_CLASS + (isOpen ? " terminal_modal--open" : "")}>
                <div className="terminal_modal__toolbar">
                    <span className="terminal_modal__title">{label || sessionId}</span>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Send Ctrl-C"
                        onClick={sendCtrlC}
                    >^C</button>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Send Ctrl-D"
                        onClick={sendCtrlD}
                    >^D</button>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Send Ctrl-Z"
                        onClick={sendCtrlZ}
                    >^Z</button>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Send Tab"
                        onClick={sendTab}
                    >⇥</button>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Arrow Up"
                        onClick={sendArrowUp}
                    >↑</button>
                    <button
                        className="terminal_modal__btn terminal_modal__btn--label"
                        title="Arrow Down"
                        onClick={sendArrowDown}
                    >↓</button>
                </div>
                <div className="terminal_modal__content">
                    <div ref={xtermRef} className="terminal_modal__xterm" />
                    <div ref={vkbSpacerRef} />
                    {contextFloatBtn}
                </div>
                <VirtualKeyboard
                    visible={vkbVisible}
                    disableAutoClose
                    onKey={handleVkbKey}
                    onHeightChange={handleVkbHeightChange}
                    onVisibilityChange={setVkbVisible}
                />
            </div>
        );
    }

    return (
        <div className={"terminal_modal " + SWIPE_GUARD_CLASS + (isOpen ? " terminal_modal--open" : "")}>
            <div className="terminal_modal__content">
                <div ref={xtermRef} className="terminal_modal__xterm" />
                {contextFloatBtn}
            </div>
        </div>
    );
});
